import type { Post } from '@/@types/post'
import Page from '@/components/Page'
import PostForm from '@/components/Post/PostForm'
import PostList from '@/components/Post/PostList'
import { Tab } from '@/components/Sidebar/config'
import { FOLLOWING_POSTS_QUERY_OPTIONS, type ListPage, cachePost } from '@/utils/fetchers/post'
import usePosts from '@/utils/hooks/usePosts'
import useTabClickListener from '@/utils/hooks/useTabClickListener'
import { prepareToRefetchListQuery } from '@/utils/list'
import queryClient from '@/utils/queryClient'
import { css } from '@emotion/react'
import { type InfiniteData, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { cloneDeep, update } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useTitle } from 'react-use'

export const Route = createFileRoute('/following')({
	component: FollowingPage,
	loader: async () => {
		await queryClient.ensureInfiniteQueryData(FOLLOWING_POSTS_QUERY_OPTIONS)
	},
})

export default function FollowingPage() {
	useTitle(`动态 - 即刻`)

	const { data, fetchNextPage, refetch, isFetching } = useSuspenseInfiniteQuery(
		FOLLOWING_POSTS_QUERY_OPTIONS
	)
	const postIds = useMemo(() => data.pages.flatMap((page) => page.data), [data.pages])
	const posts = usePosts(postIds)

	useTabClickListener(
		Tab.FOLLOWING_POST,
		useCallback(() => {
			prepareToRefetchListQuery(FOLLOWING_POSTS_QUERY_OPTIONS.queryKey)
			refetch().then(() => {
				toast.success('加载成功')
			})
		}, [refetch])
	)

	const handlePostSubmit = useCallback((newPost: Post) => {
		queryClient.setQueryData<InfiniteData<ListPage<string>>>(
			FOLLOWING_POSTS_QUERY_OPTIONS.queryKey,
			(oldData) => {
				if (!oldData) return oldData

				const newData = cloneDeep(oldData)
				cachePost(newPost)
				update(newData.pages[0], 'data', (data: Post[]) => [newPost.id, ...data])

				return newData
			}
		)
	}, [])

	return (
		<Page onReachPageBottom={fetchNextPage}>
			<div
				css={css`
					margin-top: 20px;
					padding: 20px 15px;
					box-shadow: 0 0.5px 0 0 var(--tint-separator);
				`}
			>
				<PostForm onSubmit={handlePostSubmit} />
			</div>
			<PostList data={posts} isFetching={isFetching} />
		</Page>
	)
}
